import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle, RefreshCw, Heart, Search } from 'lucide-react';
import { PropertyCard, PropertyCardSkeleton } from '../components/listings/PropertyCard';
import { Button } from '../components/ui/Button';
import { toast } from '../components/ui/Toast';
import { api, type FavoriteWithListing, ApiError } from '../services/api';
import { useAuthStore } from '../stores/auth';

export default function Favorites() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [favorites, setFavorites] = useState<FavoriteWithListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchFavorites();
  }, [isAuthenticated, navigate]);

  const fetchFavorites = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { favorites } = await api.getFavorites();
      setFavorites(favorites);
    } catch (err) {
      let errorMessage = 'Impossibile caricare i preferiti. Riprova piu tardi.';

      if (err instanceof ApiError) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast({ title: 'Errore nel caricamento', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFavoriteToggle = async (listingId: string, isFavorite: boolean) => {
    if (!isFavorite) {
      // Optimistically remove from list
      setFavorites((prev) => prev.filter((f) => f.listing.id !== listingId));
    }
  };

  // Loading state with skeletons
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-6">
          {/* Header skeleton */}
          <div className="flex items-center justify-between mb-6">
            <div className="h-7 w-40 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-5 w-24 bg-gray-200 rounded animate-pulse" />
          </div>

          {/* Grid skeleton */}
          <div className="grid gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <PropertyCardSkeleton key={i} />
            ))}
          </div>
        </div>

        {/* Spacer for mobile nav */}
        <div className="h-20 md:h-0" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-6">I miei preferiti</h1>

          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-red-100 p-4">
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Errore di caricamento
            </h3>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
              {error}
            </p>
            <Button onClick={fetchFavorites}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Riprova
            </Button>
          </div>
        </div>

        {/* Spacer for mobile nav */}
        <div className="h-20 md:h-0" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-gray-900">I miei preferiti</h1>
          {favorites.length > 0 && (
            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {favorites.length} {favorites.length === 1 ? 'annuncio' : 'annunci'}
            </span>
          )}
        </div>

        {/* Empty state */}
        {favorites.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
              <Heart className="h-10 w-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Nessun preferito salvato
            </h2>
            <p className="text-gray-500 mb-8 max-w-sm mx-auto">
              Salva i tuoi annunci preferiti per ritrovarli facilmente in questa sezione
            </p>
            <Button asChild size="lg" className="h-12">
              <Link to="/listings">
                <Search className="mr-2 h-5 w-5" />
                Esplora annunci
              </Link>
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {favorites.map((favorite) => (
              <PropertyCard
                key={favorite.id}
                listing={{ ...favorite.listing, isFavorite: true }}
                onFavoriteToggle={handleFavoriteToggle}
              />
            ))}
          </div>
        )}
      </div>

      {/* Spacer for mobile nav */}
      <div className="h-20 md:h-0" />
    </div>
  );
}
